// @flow
import * as React from 'react';
import { Box, Image, Text } from 'grommet';
import { useSelector, connect } from 'react-redux';
import { getUser as getUserSelector, getUserFullName } from 'speed-js-react';
import { bindActionCreators } from 'redux';
import * as sidePanelActions from 'esm-web-pro/redux/side-panel/actions';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';
import { AVATAR_BORDER_RADIUS } from 'esm-web-pro/styles/theme';
import UserBadge from 'esm-web-pro/components/UserBadge';
import ReactTooltip from 'react-tooltip';
import { FormattedMessage } from 'react-intl';
import { getSidePanel } from 'esm-web-pro/redux/side-panel';
import HeaderButton from 'esm-web-pro/components/layout/header/HeaderButton';
import PanelTitle from 'esm-web-pro/components/layout/header/PanelTitle';
import PresenceBox from 'esm-web-pro/containers/panels/calling/presence/PresenceBox';
import Presence from 'esm-web-pro/containers/panels/calling/presence/Presence';


const PROFILE_MENU_SIDE_PANE_KEY = 'PROFILE_MENU_SIDE_PANE_KEY';

type SidePanelActionProps = {
    openPanel: (key: string, url: ?string, sensitivity?: boolean) => { key: string, url: string },
    closePanel: (key: string, skipSensitivity?: boolean) => { key: string },
}

type Props = {
    ...$Exact<SidePanelActionProps>,
    currentUser: Object,
    fullName: String,
    actions?: Array<{
        label: React.Node,
        onClick: Function,
        key: string,
    }>,
};

const ProfileMenu = (props: Props) => {
    const {
        currentUser, fullName, actions, openPanel, closePanel,
    } = props;


    const panel = useSelector((state) => getSidePanel(state, PROFILE_MENU_SIDE_PANE_KEY));

    let label;
    let labelWithPresence;
    const buttonProps = {};
    if (currentUser && currentUser.avatar) {
        label = (
            <>
                <Image
                    data-tip
                    data-for="user-profile-avatar"
                    fit="contain"
                    width="32px"
                    height="32px"
                    src={currentUser.avatar}
                />
                <ReactTooltip
                    id="user-profile-avatar"
                    effect="solid"
                    place="left"
                    className="react-tooltip"
                >
                    <Text size="small">
                        {currentUser.username}
                    </Text>
                </ReactTooltip>
            </>
        );
        labelWithPresence = (
            <PresenceBox>
                {label}
            </PresenceBox>
        );
        buttonProps.noBorder = true;
    } else {
        label = (
            <UserBadge
                tooltipKey={`${currentUser.username}-profile-menu`}
                username={currentUser.username}
                userFullName={currentUser.userFullName
                    || `${currentUser.firstName} ${currentUser.lastName}` || currentUser.username}
                margin={null}
                tooltipPlace="left"
            />
        );
        labelWithPresence = (
            <Presence>
                {(presence) => (
                    <UserBadge
                        tooltipKey={`${currentUser.username}-profile-menu`}
                        username={currentUser.username}
                        userFullName={fullName || `${currentUser.firstName} ${currentUser.lastName}`
                            || currentUser.username}
                        margin={null}
                        tooltipPlace="left"
                        borderColor={presence}
                    />
                )}
            </Presence>
        );
    }

    const profilePanelTitle = <FormattedMessage id="profile" defaultMessage="Profile" />;

    const togglePanel = React.useCallback(() => {
        if (panel) {
            closePanel(PROFILE_MENU_SIDE_PANE_KEY);
        } else {
            openPanel(PROFILE_MENU_SIDE_PANE_KEY, profilePanelTitle, null, false, false, true);
        }
    }, [panel, openPanel, closePanel]);

    return label ? (
        <>
            <HeaderButton
                onClick={togglePanel}
                selected={!!panel}
            >
                {labelWithPresence}
            </HeaderButton>
            <SidePane
                id={PROFILE_MENU_SIDE_PANE_KEY}
                title={profilePanelTitle}
                renderTitle={() => (
                    <PanelTitle>
                        {profilePanelTitle}
                    </PanelTitle>
                )}
                closeAllOpened
                zIndex={1000}
            >
                <Box justify="between" height="100%">
                    <Box>
                        <Box direction="row" pad={{ vertical: 'small' }}>
                            <Box
                                key="avatar"
                                flex={{ shrink: 0 }}
                                width="58px"
                                height="32px"
                                align="center"
                                justify="center"
                            >
                                {label}
                            </Box>
                            <Box>
                                <Box direction="row">
                                    <Text weight={800}>
                                        {fullName}
                                    </Text>
                                </Box>
                                <Text size="12px">
                                    {currentUser.username}
                                </Text>
                            </Box>
                        </Box>
                        <Box key="profile-actions">
                            {actions.map((action) => (
                                <Box
                                    key={action.key}
                                    pad={{ horizontal: '59px', vertical: 'small' }}
                                    onClick={action.onClick}
                                    hoverIndicator="light-3"
                                >
                                    <Text size="12px">{action.label}</Text>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <ActionButtons
                        onClose={() => closePanel(PROFILE_MENU_SIDE_PANE_KEY, true)}
                        onSave={false}
                        cancelButtonLabel={<FormattedMessage defaultMessage="Close" id="close" />}
                    />
                </Box>
            </SidePane>
        </>
    ) : null;
};

ProfileMenu.defaultProps = {
    actions: [],
};

const mapStateToProps = (state: Object) => ({
    currentUser: getUserSelector(state),
    fullName: getUserFullName(state),
});

const mapDispatchToProps = (dispatch) => (
    bindActionCreators(sidePanelActions, dispatch)
);

export default connect(mapStateToProps, mapDispatchToProps)(
    ProfileMenu,
);
